import { useState } from 'react';
import { FakeLink } from '@/core/FakeLink';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import type { StoppedConditionMonitorState } from '@/vantage/types/VantageWorkbook.types';
import { Alert } from '@seeqdev/qomponents';
import { setStoppedConditionMonitorState } from '@/vantage/vantageWorkbook.actions';
import type { ScalarPropertyV1 } from '@/sdk/model/models';
import { useTranslation } from 'react-i18next';
import { canWriteItem } from '@/services/authorization.service';

interface VantageConditionMonitorStoppedWarningProps {
  stoppedConditionMonitorState: StoppedConditionMonitorState;
}

/**
 * Warning message displayed when a Vantage condition monitor is disabled and/or archived.
 */
export const VantageConditionMonitorStoppedWarning: React.FunctionComponent<
  VantageConditionMonitorStoppedWarningProps
> = ({ stoppedConditionMonitorState }) => {
  const { t } = useTranslation();

  const [show, setShow] = useState(true);

  const canEnable = canWriteItem(stoppedConditionMonitorState);

  if (!show) {
    return null;
  }

  /**
   * If a condition monitor is marked as enabled but does not have a job ID, it indicates that the job was deleted.
   * In this case, we disable the condition monitor temporarily so that it can be re-enabled properly to kick off a new job.
   */
  const disableConditionMonitorIfJobDoesNotExist = async () => {
    if (stoppedConditionMonitorState.isEnabled && !stoppedConditionMonitorState.conditionMonitorJobId) {
      await sqItemsApi.setProperties([{ name: SeeqNames.Properties.Enabled, value: false }], {
        id: stoppedConditionMonitorState.id,
      });
    }
  };

  return (
    <Alert
      id="vantageConditionMonitorStopped"
      variant="warning"
      dismissible={true}
      extraClassNames="warningBannerHeight"
      onClose={() => setShow(false)}>
      <span data-testid="vantageConditionMonitorStoppedWarning">
        {t('VANTAGE.CM_WARNING.DISABLED')}{' '}
        {canEnable ? (
          <FakeLink
            onClick={async () => {
              try {
                const isConditionMonitorDisabled =
                  !stoppedConditionMonitorState.isEnabled || !stoppedConditionMonitorState.conditionMonitorJobId;

                await disableConditionMonitorIfJobDoesNotExist();

                await sqItemsApi.setProperties(
                  new Array<ScalarPropertyV1>()
                    .concat(
                      stoppedConditionMonitorState.isArchived
                        ? [{ name: SeeqNames.Properties.Archived, value: false }]
                        : [],
                    )
                    .concat(isConditionMonitorDisabled ? [{ name: SeeqNames.Properties.Enabled, value: true }] : []),
                  { id: stoppedConditionMonitorState.id },
                );

                successToast({ messageKey: 'VANTAGE.CM_WARNING.ENABLE_SUCCESS' });
                setStoppedConditionMonitorState(undefined);
              } catch (e) {
                errorToast({ httpResponseOrError: e });
              }
            }}>
            {t('ENABLE')}
          </FakeLink>
        ) : (
          <>{t('VANTAGE.CM_WARNING.CONTACT_ADMIN')}</>
        )}
      </span>
    </Alert>
  );
};
